import mailFolderRouteHandler from './mailFolderRouteHandler';
import mailCategoryRouteHandler from './mailCategoryRouteHandler';
import mailGroupFilesRouteHandler from './mailGroupFilesRouteHandler';
import {
    mailGroupFilesSPCleanupRouteHandler,
    mailGroupFilesSXSNavigationRouteHandler,
    mailGroupFilesSxSCleanupRouteHandler,
} from './mailGroupFilesFilePreviewRouteHandlers';
import mailGroupRouteHandler from './mailGroupRouteHandler';
import mailRowRouteHandler, { mailRowFolderRouteHandler } from './mailRowRouteHandler';
import mailRowCleanupRouteHandler from './mailRowCleanupRouteHandler';
import { mailSxSCleanupRouteHandler, mailSxSNavigationRoutehandler } from './mailSxSRouteHandler';
import { inClientStoreRouteHandler } from 'owa-addins-marketplace-routing';
import personaRouteHandler from './personaRouteHandler';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { initializeDomainConnectRoutes } from 'owa-domain-connect';
import { isGroupsEnabled } from 'owa-account-capabilities/lib/isGroupsEnabled';
import { initializeOptionRoutes } from 'owa-options-router';
import {
    mailComposeCleanupRouteHandler,
    mailComposeNavigationRouteHandler,
    mailComposeBizChatRouteHandler,
} from './mailComposeRouteHandler';
import mailReadRouteHandler from './mailReadRouteHandler';
import type {
    RegisterRouteFunction,
    OnNavigateToRouteHandler,
    OnNavigateFromRouteHandler,
} from 'owa-router';
import { mailHelpPaneRouteHandler } from './mailHelpPaneRouteHandler';
import searchRouteHandler, { searchCleanupRouteHandler } from './searchRouteHandler';
import changePhotoRouteHandler from './changePhotoRouteHandler';
import mailGroupFolderRouteHandler, {
    mailGroupFolderRowRouteHandler,
} from './mailGroupFolderRouteHandler';
import feedbackPaneRouteHandler from './feedbackPaneRouteHandler';
import mailModuleRouteHandler from './mailModuleRouteHandler';
import addAccountDialogRouteHandler from './addAccountDialogRouteHandler';
import webapplinkTestRouteHandler from './webapplinkTestRouteHandler';
import { mailCopilotNavigationRouteHandler } from './mailCopilotRouteHandler';
import mailOptionsRouteHandler from './mailOptionsRouteHandler';
import { registerRouteParam } from 'owa-router/lib/routerInstance';
import { registerMcpWidgetComposeHandler } from 'owa-bizchat-context-handlers';

export function initializeMailRoutes(registerMailRoute: RegisterRouteFunction): Promise<void> {
    if (isGlobalFeatureEnabled('nh-webToAppLinksWithRouteKey')) {
        // Register custom param regexes so director-fork can differentiate
        // `:routeKey` from other parameterized paths like `:folderId` at the same trie level.
        //
        // routeKey: an account-routing identifier like "OID:<guid>@<guid>", "PUID:<hex>@<guid>", etc.
        // Values may be URL-encoded (e.g., OID%3A<guid>%40<guid>).
        // See: https://eng.ms/docs/products/m365-developer-platform/substrate-docs/Performance/Latency/Routing/Routing-best-practices
        //
        // Registering a custom param regex creates its own node in the trie used by the router,
        // preventing /:folderId's node from collapsing into /:routeKey/applink/<path> node,
        // resulting in overshadowing literal single path routes like mail/settings.
        //
        // NOTES:
        // Nodes are keyed by regex pattern, not param name.
        // Parameterized paths that do not register a custom regex use the default regex pattern, effectively sharing the same trie node.
        // Insertion order matters.
        // Same regex → same trie node → potential overshadowing bug.
        // Distinct regex → separate trie nodes → desired behavior.

        registerRouteParam(
            'routeKey',
            /((?:OID|PUID|CID|MBX|APP|SPO|SCG|MSA|AAD-UPN|AAD-SMTP|TID|SMTP|TPID)(?:%3[aA]|:)[0-9a-zA-Z_.@:%-]+)/
        );
    }

    registerMailRoute('/help', mailHelpPaneRouteHandler);
    registerMailRoute('/feedback', feedbackPaneRouteHandler);
    registerMailRoute('/addaccount', addAccountDialogRouteHandler);

    registerMailAndApplinkRoute(
        registerMailRoute,
        '/compose',
        mailComposeNavigationRouteHandler,
        mailComposeCleanupRouteHandler
    );
    registerMailAndApplinkRoute(
        registerMailRoute,
        '/compose/:draftId',
        mailComposeNavigationRouteHandler,
        mailComposeCleanupRouteHandler
    );
    // Keep original bizchat-compose route for backward compatibility, which differs from current pattern
    registerMailRoute('/applink/:routeKey/compose', mailComposeBizChatRouteHandler);

    registerMailAndApplinkRoute(registerMailRoute, '/read/:itemId', mailReadRouteHandler);
    registerMailAndApplinkRoute(
        registerMailRoute,
        '/read/account/:domain/:account/:itemId',
        mailReadRouteHandler
    );

    registerMailRoute(
        '/search/id/:rowId/sxs/:itemId/:attachmentId',
        mailSxSNavigationRoutehandler,
        searchCleanupRouteHandler
    );
    registerMailRoute(
        '/search/id/:rowId/sxs/:attachmentId',
        mailSxSNavigationRoutehandler,
        searchCleanupRouteHandler
    );
    registerMailRoute('/search/id/:rowId', mailRowRouteHandler, searchCleanupRouteHandler);
    registerMailRoute('/search', searchRouteHandler, searchCleanupRouteHandler);
    registerMailRoute('/mail/id/:rowId', mailRowFolderRouteHandler);

    initializeGroupRoutes(registerMailRoute);
    initializeOptionRoutes(registerMailRoute);
    initializeDomainConnectRoutes(registerMailRoute);

    registerMailRoute('/persons/:favoriteNodeId', personaRouteHandler);

    registerMailRoute('/category/:categoryId', mailCategoryRouteHandler);
    registerMailRoute('/inclientstore', inClientStoreRouteHandler);
    registerMailRoute('/inclientstore/assetid/:assetId', inClientStoreRouteHandler);
    registerMailRoute(
        '/inclientstore/assetid/:assetId/campaignid/:campaignId/providerid/:providerId',
        inClientStoreRouteHandler
    );
    registerMailRoute('/changephoto', changePhotoRouteHandler);

    // Route to open prompt within Copilot
    registerMailAndApplinkRoute(registerMailRoute, '/copilot', mailCopilotNavigationRouteHandler);
    registerMailAndApplinkRoute(
        registerMailRoute,
        '/copilot/:deeplink',
        mailCopilotNavigationRouteHandler
    );

    // Route to open mail settings
    registerMailAndApplinkRoute(registerMailRoute, '/settings', mailOptionsRouteHandler);
    registerMailAndApplinkRoute(registerMailRoute, '/settings/:optionKey', mailOptionsRouteHandler);

    registerMailRoute(
        '/:folderId/id/:rowId/sxs/:itemId/:attachmentId',
        mailSxSNavigationRoutehandler,
        mailSxSCleanupRouteHandler
    );
    registerMailRoute(
        '/:folderId/id/:rowId/sxs/:attachmentId',
        mailSxSNavigationRoutehandler,
        mailSxSCleanupRouteHandler
    );

    registerMailRoute(
        '/:folderId/compose/:draftId',
        mailComposeNavigationRouteHandler,
        mailComposeCleanupRouteHandler
    );

    registerMailRoute(
        '/:folderId/compose',
        mailComposeNavigationRouteHandler,
        mailComposeCleanupRouteHandler
    );

    registerMailRoute(
        '/:folderId/id/:rowId',
        mailRowFolderRouteHandler,
        mailRowCleanupRouteHandler
    );

    registerMailRoute('/:folderId', mailFolderRouteHandler, mailRowCleanupRouteHandler);

    if (isGlobalFeatureEnabled('nh-webapplinkTestPage')) {
        registerMailRoute('/applink/test', webapplinkTestRouteHandler);
    }

    registerMailAndApplinkRoute(registerMailRoute, '', mailModuleRouteHandler);
    registerMcpWidgetComposeHandler(mailComposeNavigationRouteHandler);

    return Promise.resolve();
}

// Registers a route at three paths: the base route, /applink/<base route>, and /:routeKey/applink/<base route>
function registerMailAndApplinkRoute(
    registerMailRoute: RegisterRouteFunction,
    route: string,
    handler: OnNavigateToRouteHandler,
    cleanupHandler?: OnNavigateFromRouteHandler
) {
    registerMailRoute(route, handler, cleanupHandler);
    // For the empty base route (''), this produces '/applink/' (with trailing slash); this is intentional.
    const normalizedRoute = route.startsWith('/') ? route : `/${route}`;
    registerMailRoute(`/applink${normalizedRoute}`, handler, cleanupHandler);

    if (isGlobalFeatureEnabled('nh-webToAppLinksWithRouteKey')) {
        registerMailRoute(`/:routeKey/applink${normalizedRoute}`, handler, cleanupHandler);
    }
}

function initializeGroupRoutes(registerMailRoute: RegisterRouteFunction) {
    if (isGroupsEnabled()) {
        registerMailRoute('/group/:groupDomain/:groupAlias', mailGroupRouteHandler);
        registerMailRoute('/group/:groupDomain/:groupAlias/email', mailGroupRouteHandler);
        registerMailRoute('/group/:groupDomain/:groupAlias/email/id/:rowId', mailGroupRouteHandler);
        registerMailRoute(
            '/group/:groupDomain/:groupAlias/action/:actionId',
            mailGroupRouteHandler
        );

        if (isGlobalFeatureEnabled('grp-loadFolders')) {
            registerMailRoute(
                '/group/:groupDomain/:groupAlias/email/:folderId',
                mailGroupFolderRouteHandler
            );
            registerMailRoute(
                '/group/:groupDomain/:groupAlias/email/:folderId/id/:rowId',
                mailGroupFolderRowRouteHandler
            );
        }

        initializeGroupFilesRoutes(registerMailRoute);
    }
}

function initializeGroupFilesRoutes(registerMailRoute: RegisterRouteFunction) {
    // Order matters here
    registerMailRoute(
        '/group/:groupDomain/:groupAlias/files/sxs/sp/:spFileId/?((\\w|.)*)',
        mailGroupFilesSXSNavigationRouteHandler,
        mailGroupFilesSPCleanupRouteHandler
    );

    registerMailRoute(
        '/group/:groupDomain/:groupAlias/files/sxs/attachment/:attachmentId/?((\\w|.)*)',
        mailGroupFilesSXSNavigationRouteHandler,
        mailGroupFilesSxSCleanupRouteHandler
    );

    registerMailRoute(
        '/group/:groupDomain/:groupAlias/files/?((\\w|.)*)',
        mailGroupFilesRouteHandler
    );
}
